"use client"

import { useEffect } from "react"
import WhatsappSupport from "@/components/common/whatsapp-support"
import { SubscriptionGuardProvider } from "@/features/billing/components/subscription-guard-provider"
import { useBillingStore } from "@/features/billing/store/billing-store"
// import { initChatwoot } from "@/lib/support/chatwoot"
import { AnimatePresence, motion, type Variants } from "motion/react"
import { Outlet, useLocation } from "react-router-dom"
import { Toaster } from "sonner"

// SVG Components for onboarding steps
const OnboardingSVGs = {
  profile: () => (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 800 600"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="800" height="600" fill="none" />
      <circle cx="400" cy="220" r="100" fill="currentColor" fillOpacity="0.1" />
      <circle cx="400" cy="210" r="70" fill="currentColor" fillOpacity="0.15" />
      <path
        d="M335 330C335 297.909 361.909 270 400 270C438.091 270 465 297.909 465 335V380H335V330Z"
        fill="currentColor"
        fillOpacity="0.1"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M400 190C387.85 190 378 199.85 378 212C378 224.15 387.85 234 400 234C412.15 234 422 224.15 422 212C422 199.85 412.15 190 400 190ZM366 212C366 193.22 381.22 178 400 178C418.78 178 434 193.22 434 212C434 230.78 418.78 246 400 246C381.22 246 366 230.78 366 212Z"
        fill="currentColor"
      />
      <path
        d="M280 430H520M280 460H520M280 490H420"
        stroke="currentColor"
        strokeWidth="10"
        strokeLinecap="round"
      />
      <rect x="300" y="350" width="200" height="50" rx="10" fill="currentColor" fillOpacity="0.2" />
    </svg>
  ),
  company: () => (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 800 600"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="800" height="600" fill="none" />
      <path d="M250 450V200H550V450H250Z" fill="currentColor" fillOpacity="0.05" />
      <path d="M300 450V250H500V450H300Z" fill="currentColor" fillOpacity="0.1" />
      <path
        d="M250 200H550M250 450H550"
        stroke="currentColor"
        strokeWidth="10"
        strokeLinecap="round"
      />
      <path
        d="M350 280H450M350 330H450M350 380H450"
        stroke="currentColor"
        strokeWidth="10"
        strokeLinecap="round"
      />
      <rect x="230" y="170" width="340" height="30" rx="5" fill="currentColor" fillOpacity="0.2" />
      <path
        d="M250 450V170C250 163.268 255.268 158 262 158H538C544.732 158 550 163.268 550 170V450H250Z"
        stroke="currentColor"
        strokeWidth="8"
      />
      <rect x="330" y="400" width="140" height="50" rx="8" fill="currentColor" fillOpacity="0.2" />
    </svg>
  ),
  knowledge: () => (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 800 600"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="800" height="600" fill="none" />
      <path
        d="M275 160H525C531.627 160 537 165.373 537 172V428C537 434.627 531.627 440 525 440H275C268.373 440 263 434.627 263 428V172C263 165.373 268.373 160 275 160Z"
        fill="currentColor"
        fillOpacity="0.05"
        stroke="currentColor"
        strokeWidth="6"
      />
      <path
        d="M300 200H500M300 250H450M300 300H480M300 350H420M300 400H460"
        stroke="currentColor"
        strokeWidth="10"
        strokeLinecap="round"
      />
      <path
        d="M400 460V520M340 520H460"
        stroke="currentColor"
        strokeWidth="8"
        strokeLinecap="round"
      />
      <circle
        cx="580"
        cy="220"
        r="40"
        fill="currentColor"
        fillOpacity="0.1"
        stroke="currentColor"
        strokeWidth="6"
      />
      <path
        d="M560 220H600M580 200V240"
        stroke="currentColor"
        strokeWidth="6"
        strokeLinecap="round"
      />
      <circle
        cx="580"
        cy="340"
        r="40"
        fill="currentColor"
        fillOpacity="0.1"
        stroke="currentColor"
        strokeWidth="6"
      />
      <path d="M560 340H600" stroke="currentColor" strokeWidth="6" strokeLinecap="round" />
      <path
        d="M220 300C220 278.909 237.909 261 259 261C280.091 261 298 278.909 298 300C298 321.091 280.091 339 259 339C237.909 339 220 321.091 220 300Z"
        fill="currentColor"
        fillOpacity="0.1"
        stroke="currentColor"
        strokeWidth="6"
      />
      <path
        d="M252 285L266 300L252 315"
        stroke="currentColor"
        strokeWidth="6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  preview: () => (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 800 600"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="800" height="600" fill="none" />
      <rect
        x="200"
        y="130"
        width="400"
        height="340"
        rx="20"
        fill="currentColor"
        fillOpacity="0.05"
        stroke="currentColor"
        strokeWidth="8"
      />
      <rect x="220" y="160" width="360" height="60" rx="8" fill="currentColor" fillOpacity="0.1" />
      <circle cx="250" cy="190" r="15" fill="currentColor" />
      <rect x="280" y="182" width="150" height="16" rx="4" fill="currentColor" fillOpacity="0.6" />
      <rect x="220" y="240" width="360" height="1" fill="currentColor" fillOpacity="0.2" />
      <rect x="240" y="270" width="200" height="40" rx="20" fill="currentColor" fillOpacity="0.1" />
      <rect x="250" y="284" width="120" height="12" rx="2" fill="currentColor" fillOpacity="0.5" />
      <rect x="380" y="340" width="180" height="40" rx="20" fill="currentColor" fillOpacity="0.1" />
      <rect x="420" y="354" width="120" height="12" rx="2" fill="currentColor" fillOpacity="0.5" />
      <rect x="240" y="410" width="180" height="40" rx="20" fill="currentColor" fillOpacity="0.1" />
      <rect x="250" y="424" width="120" height="12" rx="2" fill="currentColor" fillOpacity="0.5" />
    </svg>
  ),
  channels: () => (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 800 600"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="800" height="600" fill="none" />
      <circle
        cx="400"
        cy="300"
        r="150"
        fill="currentColor"
        fillOpacity="0.05"
        stroke="currentColor"
        strokeWidth="8"
      />
      <path
        d="M280 300H520"
        stroke="currentColor"
        strokeWidth="8"
        strokeLinecap="round"
        strokeDasharray="12 12"
      />
      <path
        d="M400 180V420"
        stroke="currentColor"
        strokeWidth="8"
        strokeLinecap="round"
        strokeDasharray="12 12"
      />
      <rect
        x="370"
        y="180"
        width="60"
        height="60"
        rx="10"
        fill="currentColor"
        fillOpacity="0.1"
        stroke="currentColor"
        strokeWidth="6"
      />
      <rect
        x="490"
        y="270"
        width="60"
        height="60"
        rx="10"
        fill="currentColor"
        fillOpacity="0.1"
        stroke="currentColor"
        strokeWidth="6"
      />
      <rect
        x="370"
        y="360"
        width="60"
        height="60"
        rx="10"
        fill="currentColor"
        fillOpacity="0.1"
        stroke="currentColor"
        strokeWidth="6"
      />
      <rect
        x="250"
        y="270"
        width="60"
        height="60"
        rx="10"
        fill="currentColor"
        fillOpacity="0.1"
        stroke="currentColor"
        strokeWidth="6"
      />
      <path
        d="M350 210H390M275 300H315M350 390H390M485 300H525"
        stroke="currentColor"
        strokeWidth="6"
        strokeLinecap="round"
      />
    </svg>
  ),
  default: () => (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 800 600"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="800" height="600" fill="none" />
      <path
        d="M325 180L475 180M325 200L440 200M325 250L400 250"
        stroke="currentColor"
        strokeWidth="10"
        strokeLinecap="round"
      />
      <rect
        x="250"
        y="320"
        width="300"
        height="100"
        rx="10"
        fill="currentColor"
        fillOpacity="0.05"
        stroke="currentColor"
        strokeWidth="6"
      />
      <path
        d="M270 350H350M270 390H320"
        stroke="currentColor"
        strokeWidth="6"
        strokeLinecap="round"
      />
      <circle
        cx="500"
        cy="370"
        r="30"
        fill="currentColor"
        fillOpacity="0.1"
        stroke="currentColor"
        strokeWidth="6"
      />
      <path d="M490 370H510" stroke="currentColor" strokeWidth="6" strokeLinecap="round" />
      <rect
        x="250"
        y="450"
        width="300"
        height="1"
        stroke="currentColor"
        strokeWidth="6"
        strokeLinecap="round"
      />
    </svg>
  ),
  availability: () => (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 800 600"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="800" height="600" fill="none" />
      <rect
        x="250"
        y="150"
        width="300"
        height="300"
        rx="10"
        fill="currentColor"
        fillOpacity="0.05"
        stroke="currentColor"
        strokeWidth="6"
      />
      <path
        d="M275 200H525M275 250H525M275 300H525M275 350H525M275 400H525"
        stroke="currentColor"
        strokeWidth="4"
        strokeDasharray="4 4"
      />
      <circle cx="300" cy="200" r="10" fill="currentColor" />
      <circle cx="400" cy="250" r="10" fill="currentColor" />
      <circle cx="350" cy="300" r="10" fill="currentColor" />
      <circle cx="450" cy="350" r="10" fill="currentColor" />
      <circle cx="375" cy="400" r="10" fill="currentColor" />
      <path
        d="M580 300C580 346.944 541.944 385 495 385C448.056 385 410 346.944 410 300C410 253.056 448.056 215 495 215C541.944 215 580 253.056 580 300Z"
        fill="currentColor"
        fillOpacity="0.1"
        stroke="currentColor"
        strokeWidth="6"
      />
      <path d="M495 240V300L530 335" stroke="currentColor" strokeWidth="6" strokeLinecap="round" />
      <rect
        x="250"
        y="480"
        width="300"
        height="40"
        rx="5"
        fill="currentColor"
        fillOpacity="0.1"
        stroke="currentColor"
        strokeWidth="3"
      />
      <path d="M270 500H530" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
    </svg>
  ),
}

// Definição de cores através de classes CSS para suportar temas claro e escuro
const stepBackgroundClasses = {
  profile: "bg-onboarding-profile",
  company: "bg-onboarding-company",
  knowledge: "bg-onboarding-knowledge",
  preview: "bg-onboarding-preview",
  channels: "bg-onboarding-channels",
  default: "bg-onboarding-default",
  availability: "bg-onboarding-availability",
}

// Animation variants
const imageVariants: Variants = {
  enter: {
    opacity: 0,
  },
  center: {
    opacity: 1,
    transition: {
      duration: 0.2,
      ease: "easeOut",
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.15,
      ease: "easeIn",
    },
  },
}

export default function OnboardingLayout() {
  const location = useLocation()
  const { fetchSubscriptionStatus } = useBillingStore()

  useEffect(() => {
    const fetchSubscriptionStatusEffect = async () => {
      await fetchSubscriptionStatus()
    }
    fetchSubscriptionStatusEffect()
  }, [fetchSubscriptionStatus])

  // Determine which step we're on based on the pathname
  let currentStep = "default"

  if (location.pathname.includes("/profile")) {
    currentStep = "profile"
  } else if (location.pathname.includes("/company")) {
    currentStep = "company"
  } else if (location.pathname.includes("/knowledge")) {
    currentStep = "knowledge"
  } else if (location.pathname.includes("/availability")) {
    currentStep = "availability"
  } else if (location.pathname.includes("/preview")) {
    currentStep = "preview"
  } else if (location.pathname.includes("/channels")) {
    currentStep = "channels"
  }

  const SVGComponent = OnboardingSVGs[currentStep as keyof typeof OnboardingSVGs]
  const backgroundClass = stepBackgroundClasses[currentStep as keyof typeof stepBackgroundClasses]

  return (
    <SubscriptionGuardProvider>
      <div className="flex min-h-screen">
        {/* Left side - Onboarding Content */}
        <div className="max-w-full flex-1 overflow-y-auto bg-white p-6 md:max-w-[50%] md:p-10">
          <div className="container mx-auto flex h-full max-w-4xl flex-col">
            <div className="mb-8 ml-2">
              <img src="/front-end/speeke2.svg" alt="Speeke Logo" className="h-8" />
            </div>
            <div className="flex-1">
              <Outlet />

              <WhatsappSupport />
            </div>
          </div>
        </div>

        {/* Right side - Step Image (hidden on mobile) */}
        <motion.div
          className={`hidden flex-1 items-center justify-center transition-colors duration-500 md:flex ${backgroundClass}`}
        >
          <div className="relative flex h-full w-full max-w-xl items-center justify-center p-10">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                className="text-primary h-auto w-full"
                variants={imageVariants}
                initial="enter"
                animate="center"
                exit="exit"
              >
                <SVGComponent />
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
        <Toaster richColors />
      </div>
    </SubscriptionGuardProvider>
  )
}
